import { Fragment, type FC } from "react";
import { cn } from "@/libs/utils";
import { ChevronRight, Home } from "lucide-react";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface PageHeaderProps {
	title: string;
	description?: string;
	breadcrumbs?: {
		label: string;
		href?: string;
	}[];
	actions?: React.ReactNode;
	className?: string;
}

const PageHeader: FC<PageHeaderProps> = ({
	title,
	description,
	breadcrumbs,
	actions,
	className,
}) => {
	return (
		<div className={cn("space-y-4", className)}>
			{breadcrumbs && (
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbLink href="/webapp">
								<Home className="h-4 w-4" />
								<span className="sr-only">ホーム</span>
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator>
							<ChevronRight className="h-4 w-4" />
						</BreadcrumbSeparator>
						{breadcrumbs.map((item, index) => (
							<Fragment key={item.label}>
								<BreadcrumbItem>
									{index === breadcrumbs.length - 1 ? (
										<BreadcrumbPage>{item.label}</BreadcrumbPage>
									) : (
										<BreadcrumbLink href={item.href}>
											{item.label}
										</BreadcrumbLink>
									)}
								</BreadcrumbItem>
								{index < breadcrumbs.length - 1 && (
									<BreadcrumbSeparator>
										<ChevronRight className="h-4 w-4" />
									</BreadcrumbSeparator>
								)}
							</Fragment>
						))}
					</BreadcrumbList>
				</Breadcrumb>
			)}
			<div className="flex items-start justify-between gap-4">
				<div className="space-y-1">
					<h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
					{description && (
						<p className="text-sm text-muted-foreground">{description}</p>
					)}
				</div>
				{actions && <div className="flex items-center gap-4">{actions}</div>}
			</div>
		</div>
	);
};

export { PageHeader };
export type { PageHeaderProps };
